'use client';
import React, { useRef, useEffect, useState, useCallback, ReactNode, createContext } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { clearbitFields } from '@/lib/pardot/pardot-tracking-fields';
import { useClearbitParams } from '@/lib/useUtmParams';
import { UtmHiddenFields } from '@/components/UtmHiddenFields';
import { cn } from '@/lib/utils';
import { useHash } from '@/lib/useHash';
import {
  type FormId,
  type InPageSubmitResult,
  type StrictFormId,
  IN_PAGE_SUBMIT_HEADER,
  isWorkEmailRequired,
} from '@/lib/forms/form-config';
import { looksLikePersonalEmail, WORK_EMAIL_WARNING } from '@/lib/forms/is-work-email';
import {
  CONNECTION_ERROR_MESSAGE,
  FOCUS_EMAIL_HASH,
  resolveFormErrorMessage,
  type FormErrorMessage,
} from '@/lib/forms/form-error-messages';
import { WORK_EMAIL_REASON_PARAM } from '@/lib/forms/is-work-email';
import { trackFathomEvent } from '@/lib/fathom/trackFathomEvent';
import { workEmailRejectedEvent, type WorkEmailEvents } from '@/lib/forms/work-email-tracking';
import {
  countsErrorCards,
  formErrorCardEvent,
  isFormStartTarget,
  reportTurnstileError,
  TURNSTILE_ERROR_GRACE_MS,
  type FormFunnelEvents,
  type TurnstileErrorCause,
} from '@/lib/forms/form-funnel-tracking';

/**
 * Move focus (and scroll) to the email field. The submit button can sit far below it, so
 * without this a block is silent. Shared by both work-email rejection paths — the
 * client-side blocked submit and the server-side card's "Try Again" (#work-email) — so
 * the two behave identically.
 */
function focusEmailField(form: HTMLFormElement | null) {
  const emailInput = form?.querySelector<HTMLInputElement>('input[name="email"]');
  emailInput?.focus({ preventScroll: true });
  emailInput?.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

interface FormContextValue {
  isValid: boolean;
  /** Work-email hint, surfaced as a field-level error beneath the email input
   * (EmailField reads this) so the feedback sits where the problem is — not at the
   * bottom of a long form, out of view of a submit click. */
  emailWarning: string | null;
  /** A submit was blocked by the work-email gate (cleared once the address passes). The
   * field then also says the request wasn't sent and offers the free Build Scan route. */
  emailBlocked: boolean;
  /** Fired when that Build Scan fallback link is clicked (strict forms only). */
  buildScanClickEvent?: WorkEmailEvents['build_scan_click'];
  /** Cloudflare Turnstile state, reported by TurnstileField. Submit waits for `ready`:
   * a submit without a token is rejected server-side (?error=verification-required). */
  turnstileStatus: TurnstileStatus;
  /** `cause` explains an `error` (Cloudflare code, `script-blocked`, `load-timeout`…). */
  setTurnstileStatus: (status: TurnstileStatus, cause?: TurnstileErrorCause) => void;
  /** A submit is on its way: Submit shows "Sending…", disabled. */
  submitting: boolean;
  /** The last in-page submit was rejected (SubmitButton shows it above the button). */
  submitError?: FormErrorMessage | null;
  /** TurnstileField registers how to reset its widget (null on unmount). ContactForm calls
   * it straight after a rejected submit: the server's check spent the single-use token. */
  registerTurnstileReset?: (reset: (() => void) | null) => void;
}

/** `absent` = no widget rendered (no site key, or not a Turnstile form) — never blocks.
 * `interactive` = Cloudflare is asking for a click ("Verify you are human"). */
export type TurnstileStatus = 'absent' | 'pending' | 'interactive' | 'ready' | 'error';

export const FormContext = createContext<FormContextValue>({
  isValid: false,
  emailWarning: null,
  emailBlocked: false,
  // Outside a ContactForm (newsletter forms) the widget reports into the void.
  turnstileStatus: 'absent',
  setTurnstileStatus: () => {},
  submitting: false,
});

type ContactFormProps = {
  formAction?: string;
  onSubmit?: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
} & (
  | {
      formId: StrictFormId;
      /** Strict forms' work-email gate events (co-located in fathom-events.ts). Required,
       * so a strict form can't ship unmonitored. The server-rejection event fires here
       * for an in-page submit, and from the page's error card after a no-JS submit. */
      workEmailEvents: WorkEmailEvents;
      /** Funnel + breakage events (started, Turnstile error, rejected submit). Required
       * for the same reason; `error` fires like `rejected` above. */
      funnelEvents: FormFunnelEvents;
    }
  | { formId?: Exclude<FormId, StrictFormId>; workEmailEvents?: never; funnelEvents?: never }
);

export function ContactForm({
  formAction,
  formId,
  onSubmit,
  workEmailEvents,
  funnelEvents,
  title,
  children,
  className = '',
}: ContactFormProps) {
  const resolvedAction = formId ? '/api/form-submit' : formAction!;
  const containerRef = useRef<HTMLDivElement>(null);
  const clearbitParams = useClearbitParams();
  const formRef = useRef<HTMLFormElement>(null);
  const [isValid, setIsValid] = useState(false);
  // Client-side work-email hint for strict forms (server is the enforcer).
  const strictWorkEmail = formId ? isWorkEmailRequired(formId) : false;
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [emailBlocked, setEmailBlocked] = useState(false);
  const [turnstileStatus, setTurnstileStatusState] = useState<TurnstileStatus>('absent');
  const turnstileCauseRef = useRef<TurnstileErrorCause | undefined>(undefined);
  // Stable identity: TurnstileField lists it as an effect dependency.
  const setTurnstileStatus = useCallback((status: TurnstileStatus, cause?: TurnstileErrorCause) => {
    turnstileCauseRef.current = cause;
    setTurnstileStatusState(status);
  }, []);
  // Once-per-mount guards for the gate-monitoring events.
  const warningTrackedRef = useRef(false);
  const submittedAfterWarningRef = useRef(false);
  // Email field blurred at least once (or a submit was blocked): from then on the hint
  // re-validates on every keystroke.
  const emailTouchedRef = useRef(false);
  // One submit per page view. The native POST gives no feedback while it's in flight, so
  // people clicked Submit again — and each repeat re-POSTed the same Turnstile token and
  // created a duplicate Pardot prospect. The ref (not the state) is the guard: repeats
  // can land before React re-renders the button disabled.
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<FormErrorMessage | null>(null);
  const turnstileResetRef = useRef<(() => void) | null>(null);
  // Stable identity: TurnstileField registers from an effect.
  const registerTurnstileReset = useCallback((reset: (() => void) | null) => {
    turnstileResetRef.current = reset;
  }, []);

  /**
   * Proxied forms submit in the page. A native POST reloads it on a rejection, which
   * loses the user's input and the visit's in-memory landing source
   * (src/lib/useUtmParams.ts). The route still answers with its usual 303 and fetch
   * follows it, so the outcome is read from the final URL: success → load it, exactly
   * as the native POST would; a rejection → shown above Submit, form kept. Without JS
   * the native POST and the page's `?error=` card still work.
   */
  const submitInPage = async (form: HTMLFormElement) => {
    let location: string | null = null;
    let answered = false;
    try {
      const body = new URLSearchParams([...new FormData(form)].map(([key, value]) => [key, String(value)]));
      const response = await fetch(resolvedAction, {
        method: 'POST',
        body,
        headers: { [IN_PAGE_SUBMIT_HEADER]: 'fetch' },
      });
      answered = true;
      if (response.headers.get('content-type')?.includes('application/json')) {
        const result = (await response.json().catch(() => null)) as Partial<InPageSubmitResult> | null;
        location = typeof result?.location === 'string' ? result.location : null;
      } else if (response.redirected) {
        // The no-JS 303 (a proxy or test double ignored the header): fetch followed it.
        const url = new URL(response.url);
        location = `${url.pathname}${url.search}`;
      }
    } catch {
      // Network failure: no answer (handled below).
    }
    const outcome = location ? new URL(location, window.location.origin) : null;
    if (outcome?.searchParams.get('success') === 'true') {
      // Keep the submit lock: the page is about to unload (bfcache restores release it).
      window.location.assign(`${outcome.pathname}${outcome.search}`);
      return;
    }
    // An answer without an outcome (400, a 5xx page, a WAF challenge) is a server failure
    // and counts as one; no answer at all is the visitor's connection, so it doesn't.
    const status = outcome?.searchParams.get('error') ?? (answered ? 'submission-failed' : undefined);
    const reason = outcome?.searchParams.get(WORK_EMAIL_REASON_PARAM) ?? undefined;
    // The same events the page's error card fires, once per rejected submit.
    const rejectedEvent = workEmailEvents && workEmailRejectedEvent(status, reason, workEmailEvents);
    if (rejectedEvent) trackFathomEvent(rejectedEvent);
    const errorEvent = funnelEvents && formErrorCardEvent(status, funnelEvents);
    if (errorEvent && countsErrorCards(window.location.hostname)) trackFathomEvent(errorEvent);

    submittingRef.current = false;
    setSubmitting(false);
    setSubmitError(answered ? resolveFormErrorMessage(status) : CONNECTION_ERROR_MESSAGE);
    // The spent token must not be re-posted: reset the widget now (same tick as releasing
    // the lock), so Submit waits for the fresh token it fetches.
    turnstileResetRef.current?.();
  };

  // A rejected submit: move focus (and the view) to the explanation above Submit, so it
  // is announced and in sight; it clears once the user changes a field.
  useEffect(() => {
    const form = formRef.current;
    if (!submitError || !form) return;
    const alert = form.querySelector<HTMLElement>('[data-testid="form-error-inline"]');
    alert?.focus({ preventScroll: true });
    alert?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const clear = (event: Event) => {
      if (isFormStartTarget(event.target)) setSubmitError(null);
    };
    form.addEventListener('input', clear);
    form.addEventListener('change', clear);
    return () => {
      form.removeEventListener('input', clear);
      form.removeEventListener('change', clear);
    };
  }, [submitError]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (submittingRef.current) {
      event.preventDefault();
      return;
    }
    if (strictWorkEmail) {
      const email = new FormData(event.currentTarget).get('email');
      if (looksLikePersonalEmail(typeof email === 'string' ? email : '')) {
        setEmailWarning(WORK_EMAIL_WARNING);
        setEmailBlocked(true);
        // Enter-to-submit never blurs the field, so mark it touched here or the hint
        // (and "wasn't sent") would stay stale while the address is fixed.
        emailTouchedRef.current = true;
        event.preventDefault();
        // Every blocked click counts — the gate's block rate was invisible before.
        if (workEmailEvents) trackFathomEvent(workEmailEvents.blocked);
        // The field-level warning (role="alert") is announced to screen readers, and
        // focusing the field puts it in view for everyone.
        focusEmailField(event.currentTarget);
        return;
      }
      // Passed the gate after seeing the hint: warning-shown minus this ≈ drop-off.
      if (workEmailEvents && warningTrackedRef.current && !submittedAfterWarningRef.current) {
        submittedAfterWarningRef.current = true;
        trackFathomEvent(workEmailEvents.submitted_after_warning);
      }
    }
    submittingRef.current = true;
    setSubmitting(true);
    if (onSubmit) {
      onSubmit();
    }
    if (formId) {
      event.preventDefault();
      setSubmitError(null);
      void submitInPage(event.currentTarget);
    }
  };

  // Back-navigation from the success/error page can restore this page from the bfcache
  // with the lock still on — release it so the form is usable again.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      submittingRef.current = false;
      setSubmitting(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    const handleValidation = () => {
      setIsValid(form.checkValidity());
    };

    form.addEventListener('input', handleValidation);
    return () => form.removeEventListener('input', handleValidation);
  }, []);

  // On strict forms, surface the work-email hint on blur — and, once the field has
  // been blurred at least once, keep it live on every change (so it clears the moment
  // the user fixes the domain). Never mid-first-keystroke: we wait for the first blur
  // so a half-typed address isn't flagged. Server stays authoritative.
  useEffect(() => {
    if (!strictWorkEmail) return;
    const form = formRef.current;
    if (!form) return;
    const emailInput = form.querySelector<HTMLInputElement>('input[name="email"]');
    if (!emailInput) return;

    const validate = () => {
      const personal = looksLikePersonalEmail(emailInput.value);
      setEmailWarning(personal ? WORK_EMAIL_WARNING : null);
      // A fixed address clears the "wasn't sent" state; a still-personal one keeps it.
      if (!personal) setEmailBlocked(false);
    };
    const handleBlur = () => {
      emailTouchedRef.current = true;
      validate();
    };
    const handleInput = () => {
      if (emailTouchedRef.current) validate();
    };

    emailInput.addEventListener('blur', handleBlur);
    emailInput.addEventListener('input', handleInput);
    return () => {
      emailInput.removeEventListener('blur', handleBlur);
      emailInput.removeEventListener('input', handleInput);
    };
  }, [strictWorkEmail]);

  // Funnel start: the first real input (keystroke, select change, checkbox) in this form,
  // once per mount. Not focus — the trial page's sticky CTA auto-focuses the email field.
  const startedEvent = funnelEvents?.started;
  const startedTrackedRef = useRef(false);
  useEffect(() => {
    const form = formRef.current;
    if (!form || !startedEvent) return;
    const handleStart = (event: Event) => {
      if (startedTrackedRef.current || !isFormStartTarget(event.target)) return;
      startedTrackedRef.current = true;
      trackFathomEvent(startedEvent);
    };
    form.addEventListener('input', handleStart);
    form.addEventListener('change', handleStart);
    return () => {
      form.removeEventListener('input', handleStart);
      form.removeEventListener('change', handleStart);
    };
  }, [startedEvent]);

  // The security check failed (blocked script, load timeout, widget error): count it once
  // per mount, and send the cause to Sentry. Production hosts only (see reportTurnstileError).
  const turnstileErrorEvent = funnelEvents?.turnstile_error;
  const turnstileErrorTrackedRef = useRef(false);
  useEffect(() => {
    if (turnstileStatus !== 'error' || !turnstileErrorEvent || !formId || turnstileErrorTrackedRef.current) return;
    // Only if it stays failed: any later status (a late load, the widget's own retry
    // succeeding) clears the timer, so a recovered check never counts.
    const cause = turnstileCauseRef.current;
    const timer = setTimeout(() => {
      turnstileErrorTrackedRef.current = true;
      reportTurnstileError(formId, turnstileErrorEvent, cause);
    }, TURNSTILE_ERROR_GRACE_MS);
    return () => clearTimeout(timer);
  }, [turnstileStatus, turnstileErrorEvent, formId]);

  // First time the hint renders (blur, live re-validate, or a blocked submit), once per
  // mount — how many people see it, vs. how many then submit anyway (blocked).
  const warningShownEvent = workEmailEvents?.warning_shown;
  useEffect(() => {
    if (!emailWarning || !warningShownEvent || warningTrackedRef.current) return;
    warningTrackedRef.current = true;
    trackFathomEvent(warningShownEvent);
  }, [emailWarning, warningShownEvent]);

  // Returning from the server-side work-email rejection card: "Try Again" links back to
  // the form at #work-email, so land the user on the one field they have to change. The
  // card already explained why, so no inline warning is pre-set on an empty field.
  //
  // Deferred by a frame: the App Router resets focus to the document after a client-side
  // navigation commits, which lands AFTER this effect and would swallow the focus call.
  const hash = useHash();
  useEffect(() => {
    if (hash !== FOCUS_EMAIL_HASH) return;
    const frame = requestAnimationFrame(() => focusEmailField(formRef.current));
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  useGSAP(
    () => {
      // Forms built from other field components (e.g. kraidle) have nothing to animate.
      const targets = gsap.utils.toArray('.form-animate-in', containerRef.current);
      if (!targets.length) return;
      gsap.to(targets, {
        x: 0,
        delay: 0.5,
        opacity: 1,
        duration: 0.8,
        stagger: 0.08,
        ease: 'power1.out',
      });
    },
    { scope: containerRef },
  );

  return (
    <FormContext.Provider
      value={{
        isValid,
        emailWarning,
        emailBlocked,
        buildScanClickEvent: workEmailEvents?.build_scan_click,
        turnstileStatus,
        setTurnstileStatus,
        submitting,
        submitError,
        registerTurnstileReset,
      }}
    >
      <div ref={containerRef} className={cn('overflow-hidden rounded-2xl', className)}>
        <div className="p-6">
          {title && <h6 className="form-animate-in mb-4 text-eyebrow-strong">{title}</h6>}
          <form
            ref={formRef}
            action={resolvedAction}
            method="post"
            className="grid grid-cols-2 gap-2 text-body md:gap-4"
            onSubmit={handleSubmit}
          >
            {formId && <input type="hidden" name="_formId" value={formId} />}
            {/* The work-email warning renders inside EmailField (field-level), so it sits
                next to the email input rather than at the bottom of the form. */}
            {children}

            {/* Hidden UTM and Clearbit fields */}
            <UtmHiddenFields />
            {clearbitFields.map(field => (
              <input key={field} type="hidden" name={field} value={clearbitParams[field]} />
            ))}
          </form>
        </div>
      </div>
    </FormContext.Provider>
  );
}
